import type L1ReviewRepository from "@/core/compliance/repositories/L1ReviewRepository";
import type { L1ReviewItem, L1ReviewStatus } from "@/core/compliance/entities/L1Review";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import type { PaginatedResult } from "@/core/shared/types/PaginatedResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import {
  executeRequest,
  executeVoidRequest,
} from "../../http/services/executeRequest/executeRequest";
import l1ReviewEndpoints from "../../api/l1ReviewEndpoints/l1ReviewEndpoints";
import { type L1ReviewItemDto, mapL1ReviewDtoToEntity } from "./mappers";

/** Enveloppe paginée renvoyée par le backend (`{ items, totalElements }`). */
interface PagedDto {
  items?: L1ReviewItemDto[];
  totalElements?: number;
}

export default class L1ReviewRepositoryBackend implements L1ReviewRepository {
  constructor(private readonly httpClient: HttpClient) {}

  /** Mapping partagé enveloppe paginée backend → PaginatedResult<L1ReviewItem> (DRY). */
  private toPagedResult(paged: PagedDto | undefined): PaginatedResult<L1ReviewItem> {
    return {
      items: (paged?.items ?? []).map(mapL1ReviewDtoToEntity),
      totalElements: paged?.totalElements ?? 0,
    };
  }

  private fetchPage(
    url: string,
  ): Promise<RepositoryResult<PaginatedResult<L1ReviewItem>>> {
    return executeRequest(
      () => this.httpClient.get<ApiResponse<PagedDto>>({ url, withAuth: true }),
      (paged) => this.toPagedResult(paged),
    );
  }

  async getPendingReviews(
    page: number,
    size: number,
  ): Promise<RepositoryResult<PaginatedResult<L1ReviewItem>>> {
    return this.fetchPage(l1ReviewEndpoints.pendingPaged(page, size));
  }

  async getReviewsByStatus(
    status: L1ReviewStatus,
    page: number,
    size: number,
  ): Promise<RepositoryResult<PaginatedResult<L1ReviewItem>>> {
    return this.fetchPage(l1ReviewEndpoints.byStatusPaged(status, page, size));
  }

  async authorizeReview(reviewId: string): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<void>, Record<string, never>>({
        url: l1ReviewEndpoints.authorize(reviewId),
        body: {},
        withAuth: true,
      }),
    );
  }

  async suspendReview(reviewId: string, reason: string): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<void>, { reason: string }>({
        url: l1ReviewEndpoints.suspend(reviewId),
        body: { reason },
        withAuth: true,
      }),
    );
  }

  async getReviewById(reviewId: string): Promise<RepositoryResult<L1ReviewItem>> {
    return executeRequest(
      () =>
        this.httpClient.get<ApiResponse<L1ReviewItemDto>>({
          url: l1ReviewEndpoints.byId(reviewId),
          withAuth: true,
        }),
      mapL1ReviewDtoToEntity,
    );
  }

  async rejectReview(reviewId: string, motif: string): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<void>, { motif: string }>({
        url: l1ReviewEndpoints.reject(reviewId),
        body: { motif },
        withAuth: true,
      }),
    );
  }

  async assignReview(reviewId: string): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<void>, Record<string, never>>({
        url: l1ReviewEndpoints.assign(reviewId),
        body: {},
        withAuth: true,
      }),
    );
  }

  async escalateReview(reviewId: string): Promise<RepositoryResult<void>> {
    return executeVoidRequest(() =>
      this.httpClient.post<ApiResponse<void>, Record<string, never>>({
        url: l1ReviewEndpoints.escalate(reviewId),
        body: {},
        withAuth: true,
      }),
    );
  }
}
